import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ThreadArt } from "@/components/ThreadArt";
import { useSEO } from "@/components/Seo";
import { eyebrow } from "@/lib/styles";

const SPANS = [
  "col-span-2 row-span-2",
  "",
  "row-span-2",
  "",
  "",
  "col-span-2",
  "row-span-2",
  "",
];

export default function Gallery() {
  useSEO(
    "Gallery | Threads Unbound™",
    "Original Threads Unbound™ artwork: gold thread studies, silhouettes, and cosmic ink. The visual heart of the brand."
  );
  const [artworks, setArtworks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? artworks : artworks.filter((a) => a.pillar === filter);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const p = (searchParams.get("pillar") || "").toLowerCase();
    if (["freedom", "chaos", "truth", "grit", "rebellion", "wisdom"].includes(p)) setFilter(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const FILTERS = [
    { key: "all", label: "All" },
    { key: "freedom", label: "Freedom" },
    { key: "chaos", label: "Chaos" },
    { key: "truth", label: "Truth" },
    { key: "grit", label: "Grit" },
    { key: "rebellion", label: "Rebellion" },
    { key: "wisdom", label: "Wisdom" },
  ];

  useEffect(() => {
    api
      .get("/artworks")
      .then((r) => setArtworks(r.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (selected === null) return;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") setSelected(null);
      if (e.key === "ArrowRight") setSelected((s) => (s + 1) % filtered.length);
      if (e.key === "ArrowLeft") setSelected((s) => (s - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [selected, filtered.length]);

  const current = selected !== null ? filtered[selected] : null;

  return (
    <main className="px-6 pb-32 pt-36 md:px-12 md:pt-44 lg:px-20" data-testid="gallery-page">
      <header>
        <MaskedLine>
          <span className={eyebrow}>The work</span>
        </MaskedLine>
        <h1 className="mt-6 font-serif leading-[0.95] tracking-tighter">
          <MaskedLine delay={0.15}>
            <span className="block text-5xl font-light sm:text-6xl lg:text-7xl">
              The <span className="italic text-[#D4AF37]">Gallery</span>
            </span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60">
            Studies in thread, silhouette, and static. Tap any piece to see it full-screen with its story.
          </p>
        </Reveal>
      </header>

      <Reveal delay={0.4}>
        <div className="mt-12 flex flex-wrap gap-3" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`gallery-filter-${f.key}`}
              onClick={() => {
                setFilter(f.key);
                setSelected(null);
              }}
              className={`border px-5 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                filter === f.key
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-white/15 text-white/40 hover:border-white/40 hover:text-white/70"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Reveal>

      <section
        className="mt-14 grid auto-rows-[160px] grid-cols-2 gap-3 md:auto-rows-[220px] md:grid-cols-4 md:gap-4"
        data-testid="gallery-grid"
      >
        {filtered.map((a, i) => (
          <Reveal key={a.id} delay={(i % 4) * 0.06} className={SPANS[i % SPANS.length]}>
            <figure
              className="group relative h-full w-full cursor-pointer overflow-hidden border border-white/10"
              data-testid={`artwork-${a.id}`}
              role="button"
              tabIndex={0}
              aria-label={`View ${a.title} full-screen`}
              onClick={() => setSelected(i)}
              onKeyDown={(e) => e.key === "Enter" && setSelected(i)}
            >
              {a.image_url ? (
                <img
                  src={a.image_url}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              ) : (
                <ThreadArt
                  seed={`artwork-${a.title}`}
                  className="h-full w-full transition-transform duration-700 ease-out group-hover:scale-105"
                />
              )}
              <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 bg-black/70 p-4 opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                {a.pillar && (
                  <p className="mb-1 text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/80">{a.pillar}</p>
                )}
                <p className="font-serif text-lg font-light">{a.title}</p>
                {a.description && <p className="mt-1 text-xs text-white/50">{a.description}</p>}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </section>

      {loaded && filtered.length === 0 && (
        <p className="mt-20 text-white/40" data-testid="gallery-empty">
          Nothing woven under this pillar yet.
        </p>
      )}

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="artwork-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/92 p-6 backdrop-blur-md"
            style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
            onClick={() => setSelected(null)}
          >
            <button
              data-testid="lightbox-close"
              aria-label="Close"
              onClick={() => setSelected(null)}
              className="absolute right-6 top-6 border border-white/20 p-2.5 text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              data-testid="lightbox-prev"
              aria-label="Previous artwork"
              onClick={(e) => {
                e.stopPropagation();
                setSelected((selected - 1 + filtered.length) % filtered.length);
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 border border-white/20 p-2.5 text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] md:left-8"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.figure
              key={current.id}
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              {current.image_url ? (
                <img
                  src={current.image_url}
                  alt={current.title}
                  data-testid="lightbox-image"
                  className="mx-auto max-h-[72vh] w-auto max-w-full border border-white/15 object-contain"
                />
              ) : (
                <ThreadArt seed={`artwork-${current.title}`} className="mx-auto h-[60vh] w-[80vw] max-w-3xl border border-white/15" />
              )}
              <figcaption className="mt-5">
                {current.pillar && (
                  <p className="mb-2 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]" data-testid="lightbox-pillar">
                    {current.pillar}
                  </p>
                )}
                <p className="font-serif text-2xl font-light md:text-3xl" data-testid="lightbox-title">
                  {current.title}
                </p>
                {current.description && (
                  <p className="mt-2 text-sm italic text-[#D4AF37]/80" data-testid="lightbox-description">
                    {current.description}
                  </p>
                )}
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              aria-label="Next artwork"
              onClick={(e) => {
                e.stopPropagation();
                setSelected((selected + 1) % filtered.length);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 border border-white/20 p-2.5 text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] md:right-8"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
